// ============================================
// Video Tile Component
// Individual participant video display
// ============================================

import React from 'react';
import {
  useHMSStore,
  useVideo,
  selectIsPeerAudioEnabled,
  selectIsPeerVideoEnabled,
  selectConnectionQualityByPeerID
} from '@100mslive/react-sdk';

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Connection quality indicator
 */
const ConnectionQuality = ({ quality }) => {
  if (!quality) return null;

  const bars = quality.downlinkQuality || 0;
  const colors = {
    0: 'bg-red-500',
    1: 'bg-red-500',
    2: 'bg-yellow-500',
    3: 'bg-green-500',
    4: 'bg-green-500',
    5: 'bg-green-500'
  };

  return (
    <div className="flex items-end space-x-0.5 h-3">
      {[1, 2, 3, 4, 5].map((bar) => (
        <div
          key={bar}
          className={`w-1 rounded-sm ${
            bar <= bars ? colors[bars] : 'bg-gray-600'
          }`}
          style={{ height: `${bar * 20}%` }}
        />
      ))}
    </div>
  );
};

/**
 * Video Tile - displays a single participant's video
 */
const VideoTile = ({ peer, isLocal, isLarge }) => {
  const { videoRef } = useVideo({
    trackId: peer.videoTrack
  });

  const isAudioEnabled = useHMSStore(selectIsPeerAudioEnabled(peer.id));
  const isVideoEnabled = useHMSStore(selectIsPeerVideoEnabled(peer.id));
  const connectionQuality = useHMSStore(selectConnectionQualityByPeerID(peer.id));

  // Parse display name from peer
  const displayName = peer.name || 'Participant';
  const userType = peer.metadata?.userType || displayName.split(':')[0];

  return (
    <div className={`
      relative w-full h-full rounded-xl overflow-hidden
      ${isLocal ? 'ring-2 ring-blue-500' : ''}
      bg-gray-800
    `}>
      {/* Video element */}
      {isVideoEnabled ? (
        <video
          ref={videoRef}
          autoPlay
          muted={isLocal}
          playsInline
          className={`w-full h-full object-cover ${isLocal ? 'transform scale-x-[-1]' : ''}`}
        />
      ) : (
        // Avatar placeholder when video is off
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className={`
            ${isLarge ? 'w-32 h-32 text-4xl' : 'w-16 h-16 text-xl'}
            rounded-full bg-gradient-to-br from-blue-500 to-purple-600
            flex items-center justify-center text-white font-semibold
          `}>
            {getInitials(displayName)}
          </div>
        </div>
      )}

      {/* Gradient overlay for name */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 p-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {/* Mute indicator */}
          {!isAudioEnabled && (
            <span className="p-1 bg-red-500 rounded-full">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            </span>
          )}

          {/* Name */}
          <span className="text-white text-sm font-medium truncate max-w-[120px]">
            {displayName}
            {isLocal && ' (You)'}
          </span>
        </div>

        {/* Connection quality */}
        <ConnectionQuality quality={connectionQuality} />
      </div>

      {/* Speaking indicator */}
      {peer.isHandRaised && (
        <div className="absolute top-2 right-2 p-1.5 bg-yellow-500 rounded-full animate-bounce">
          <span className="text-lg">✋</span>
        </div>
      )}

      {/* Role badge */}
      {peer.roleName === 'host' && (
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
            Host
          </span>
        </div>
      )}
    </div>
  );
};

export default VideoTile;
